import { TextValidatorType } from "anketa-core/base";
import { Group, Row, Section } from "anketa-core/group";
import { Translation } from "anketa-core/i18n";
import { NumericQuestion } from "anketa-core/numeric_question";
import { OpenQuestion } from "anketa-core/open_question";
import { OptionsQuestion } from "anketa-core/options_question";
import { TextValidator } from "anketa-core/validation";

export const setupNetworkGroup = (networkScanners: Section, idx = 0) => {
  const networkGroup = opsanioNetworkScan(idx);
  networkScanners.addChild(networkGroup.opsanioNetworkScan);

  function onConfigChange() {
    const name = networkGroup.networkName.fact.raw;
    const cidr = networkGroup.networkCidr.fact.raw;

    if (
      name &&
      name.toString().trim() !== "" &&
      cidr &&
      cidr.toString().trim() !== ""
    ) {
      networkGroup.networkName.removeChangeListener(onConfigChange);
      networkGroup.networkCidr.removeChangeListener(onConfigChange);
      setupNetworkGroup(networkScanners, idx + 1);
    }
  }

  networkGroup.networkName.addChangeListener(onConfigChange);
  networkGroup.networkCidr.addChangeListener(onConfigChange);
};

export const opsanioNetworkScan = (
  idx: number
): {
  opsanioNetworkScan: Group;
  networkCidr: OpenQuestion;
  networkName: OpenQuestion;
} => {
  const idPrefix = `network_scanner_${idx}`;

  const opsanioNetworkScan = new Group(idPrefix, [
    new Translation("en", "Unconfigured network"),
    new Translation("de", "Unkonfiguriertes Netz"),
  ]);

  const common = new Row(`${idPrefix}_common`, [
    new Translation("en", "General"),
    new Translation("de", "Allgemein"),
  ]);
  opsanioNetworkScan.addChild(common);

  const networkName = new OpenQuestion(`${idPrefix}_name`, [
    new Translation("en", "Display Name"),
    new Translation("de", "Anzeigename"),
  ]);
  networkName.required = true;
  networkName.validator = new TextValidator();
  networkName.addChangeListener(() => {
    const plainName = networkName.fact.raw.toString();
    if (plainName.trim() !== "") {
      opsanioNetworkScan.title = [
        new Translation("en", plainName),
        new Translation("de", plainName),
      ];
    } else {
      opsanioNetworkScan.title = [
        new Translation("en", "Unconfigured network"),
        new Translation("de", "Unkonfiguriertes Netz"),
      ];
    }
  });

  common.addChild(networkName);

  const networkCidr = new OpenQuestion(`${idPrefix}_cidr`, [
    new Translation("en", "Network CIDR"),
    new Translation("de", "Netzwerk CIDR"),
  ]);
  networkCidr.required = true;
  networkCidr.validator = new TextValidator(
    TextValidatorType.PRIVATE_IPV4_NETWORK_CIDR
  );
  common.addChild(networkCidr);

  const scanInterval = new NumericQuestion(`${idPrefix}_interval`, [
    new Translation("en", "Scan interval"),
    new Translation("de", "Scan-Intervall"),
  ]);
  scanInterval.required = true;
  if (scanInterval.fact.raw === null) {
    scanInterval.setFactValue(45);
  }
  common.addChild(scanInterval);

  const readFingerprint = new OptionsQuestion(
    `${idPrefix}_fingerprint`,
    [
      new Translation("en", "Enable Fingerprint"),
      new Translation("de", "Fingerprint aktivieren"),
    ],
    "default.options.yes_no"
  );
  readFingerprint.help = [
    new Translation(
      "en",
      "Do you want opsanio to be able to read the fingerprints in the network?"
    ),
    new Translation(
      "de",
      "Soll opsanio in der Lage sein, die Fingerprint im Netzwerk zu lesen?"
    ),
  ];
  if (readFingerprint.fact.raw === null) {
    readFingerprint.setFactValue(0);
  }
  opsanioNetworkScan.addChild(readFingerprint);

  const readNetBios = new OptionsQuestion(
    `${idPrefix}_netbios`,
    [
      new Translation("en", "Enable NetBios"),
      new Translation("de", "NetBios aktivieren"),
    ],
    "default.options.yes_no"
  );
  readNetBios.help = [
    new Translation(
      "en",
      "Do you want opsanio to be able to read the NetBios data in the network?"
    ),
    new Translation(
      "de",
      "Soll opsanio in der Lage sein, die NetBios Daten im Netzwerk zu lesen?"
    ),
  ];
  if (readNetBios.fact.raw === null) {
    readNetBios.setFactValue(0);
  }
  opsanioNetworkScan.addChild(readNetBios);

  const readSnmp = new OptionsQuestion(
    `${idPrefix}_snmp`,
    [
      new Translation("en", "Enable SNMP"),
      new Translation("de", "SNMP aktivieren"),
    ],
    "default.options.yes_no"
  );
  readSnmp.help = [
    new Translation(
      "en",
      "Should opsanio be able to make SNMP queries in the network?"
    ),
    new Translation(
      "de",
      "Soll opsanio in der Lage sein, die SNMP-Abfragen im Netzwerk zu tätigen?"
    ),
  ];
  if (readSnmp.fact.raw === null) {
    readSnmp.setFactValue(0);
  }
  const snmpV3 = new OptionsQuestion(
    `${idPrefix}_snmp_v3`,
    [new Translation("en", "SNMPv3"), new Translation("de", "SNMPv3")],
    "default.options.yes_no"
  );
  if (snmpV3.fact.raw === null) {
    snmpV3.setFactValue(0);
  }
  const snmpSettings = new Row(`${idPrefix}_snmp_settings`, [
    new Translation("en", "SNMP Settings"),
    new Translation("de", "SNMP Einstellungen"),
  ]);
  snmpSettings.visible = readSnmp.fact.raw === "0";
  const snmpSettingsUser = new OpenQuestion(`${idPrefix}_snmp_settings_user`, [
    new Translation("en", "Username"),
    new Translation("de", "Nutzername"),
  ]);

  const snmpSettingsPassword = new OpenQuestion(
    `${idPrefix}_snmp_settings_psw`,
    [new Translation("en", "Password"), new Translation("de", "Password")]
  );
  snmpSettingsPassword.readonly = true;
  snmpSettingsPassword.help = [
    new Translation("en", "The password is exchanged via a separate channel."),
    new Translation(
      "de",
      "Der Austausch des Passworts erfolgt über einen gesonderten Kanal."
    ),
  ];

  const snmpSettingsCommunityString = new OpenQuestion(
    `${idPrefix}_snmp_settings_str`,
    [
      new Translation("en", "Community string"),
      new Translation("de", "Community string"),
    ]
  );

  readSnmp.addChangeListener(() => {
    snmpSettings.visible = readSnmp.fact.raw === "0";
  });
  snmpV3.addChangeListener(() => {
    snmpSettingsUser.visible = snmpV3.fact.raw === "0";
    snmpSettingsPassword.visible = snmpV3.fact.raw === "0";
  });

  snmpSettings.addChild(snmpV3);
  snmpSettings.addChild(snmpSettingsUser);
  snmpSettings.addChild(snmpSettingsCommunityString);
  snmpSettings.addChild(snmpSettingsPassword);

  snmpSettingsUser.validator = new TextValidator();
  snmpSettingsPassword.validator = new TextValidator();
  snmpSettingsCommunityString.validator = new TextValidator();

  opsanioNetworkScan.addChild(readSnmp);
  opsanioNetworkScan.addChild(snmpSettings);

  return { opsanioNetworkScan: opsanioNetworkScan, networkCidr, networkName };
};
